<template>
  <Dialog
    :model-value="visible"
    title="Confirm Send Documents"
    dialog-type="primary"
    dialog-size="small"
    title-variant="medium"
    :header-icon-src="require('@/assets/MyPatient/document_white.svg')"
    :close-on-overlay="false"
    @update:model-value="onDialogUpdate"
  >
    <!-- Body -->
    <div class="confirm-send-documents-body">
      <!-- Warning Message -->
      <div class="warning-message">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="warning-icon">
          <path d="M1 21H23L12 2L1 21ZM13 18H11V16H13V18ZM13 14H11V10H13V14Z" fill="#fa8c16"/>
        </svg>
        <div class="warning-content">
          <p class="warning-title">Please review the documents you are about to send</p>
          <p class="warning-text">
            Make sure you are not sending patient information to the wrong person. This action cannot be undone.
          </p>
        </div>
      </div>

      <!-- Document Count -->
      <Notification
        :message="`${documents.length} document${documents.length !== 1 ? 's' : ''} will be sent.`"
        theme="info"
        :with_close_btn="false"
      />

      <!-- Document List -->
      <div class="document-list">
        <div
          v-for="(document, index) in documents"
          :key="index"
          class="document-item"
        >
          <div class="document-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C4.9 2 4.01 2.9 4.01 4L4 20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM16 18H8V16H16V18ZM16 14H8V12H16V14ZM13 9V3.5L18.5 9H13Z" fill="#5587BC"/>
            </svg>
          </div>
          <div class="document-info">
            <div class="document-name">{{ document.name }}</div>
            <div v-if="document.type || document.createdAt" class="document-meta">
              <span v-if="document.type">{{ document.type }}</span>
              <span v-if="document.type && document.createdAt"> • </span>
              <span v-if="document.createdAt">{{ formatTimestamp(document.createdAt) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="confirm-send-documents-footer-leading">
        <ResponsiveButton
          button_type="outlined"
          built_in_theme="error"
          size="medium"
          display_name="Cancel"
          @click-button="$emit('close')"
        />
      </div>
      <div class="confirm-send-documents-footer-actions">
        <ResponsiveButton
          button_type="filled"
          built_in_theme="dark"
          size="medium"
          display_name="Confirm & Send"
          @click-button="confirmSend"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import moment from 'moment';

export default {
  name: 'ConfirmSendDocumentsDialog',
  emits: ['update:modelValue', 'close', 'confirm'],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    documents: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    confirmSend() {
      this.$emit('confirm');
    },
    onDialogUpdate(value) {
      this.$emit('update:modelValue', value);
      if (!value) {
        this.$emit('close');
      }
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      return moment(timestamp).format('MMM D, YYYY');
    }
  }
};
</script>

<style scoped>
.confirm-send-documents-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  gap: 16px;
}

.warning-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 8px;
}

.warning-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.warning-content {
  flex: 1;
}

.warning-title {
  font-weight: 600;
  font-size: 16px;
  color: #333333;
  margin: 0 0 8px 0;
}

.warning-text {
  font-weight: 400;
  font-size: 14px;
  color: #666666;
  margin: 0;
}

.document-list {
  background: white;
  border-radius: 8px;
  border: 1px solid #E6E6E6;
  max-height: 400px;
  overflow-y: auto;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #E6E6E6;
  transition: background-color 0.2s;
}

.document-item:last-child {
  border-bottom: none;
}

.document-item:hover {
  background: #F9FBFD;
}

.document-icon {
  flex-shrink: 0;
}

.document-info {
  flex: 1;
  min-width: 0;
}

.document-name {
  font-weight: 500;
  font-size: 14px;
  color: #333333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.document-meta {
  font-weight: 400;
  font-size: 12px;
  color: #999999;
  margin-top: 4px;
}

.confirm-send-documents-footer-leading {
  display: flex;
  align-items: center;
}

.confirm-send-documents-footer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-left: auto;
}

/* Scrollbar styling */
.document-list::-webkit-scrollbar {
  width: 6px;
}

.document-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.document-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.document-list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
